import { useEffect, useState } from 'react'
import { useMutation } from '@apollo/client'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { CREATE_PAYMENT_INTENT, FINALIZE_STRIPE_CHECKOUT } from '../../graphql/operations'
import { useCart } from '../../lib/cart'
import { formatPrice } from '../../lib/format'
import { stripePromise, stripeConfigured } from '../../lib/stripe'

export interface PaidOrder { orderNumber: string; total: number; currency: string; email: string }
interface Props { onPaid: (order: PaidOrder) => void }

// Real Stripe Elements card flow for signed-in shoppers:
//   createPaymentIntent → <PaymentElement> → confirmPayment → finalize.
// The PaymentIntent is created for the user's cart server-side; we only
// ever hold the client_secret (no card data touches our code).
export default function StripeCheckout({ onPaid }: Props) {
  const { total, currency } = useCart()
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [createPaymentIntent, { loading }] = useMutation(CREATE_PAYMENT_INTENT)

  useEffect(() => {
    let cancelled = false
    createPaymentIntent({ variables: { currency: currency.toLowerCase() } })
      .then(({ data }) => {
        if (cancelled) return
        const p = data?.createPaymentIntent
        const err = p?.userErrors?.[0]?.message
        if (err) { setError(err); return }
        if (!p?.clientSecret || !p?.paymentIntentId) {
          setError('Could not initialize payment.'); return
        }
        setClientSecret(p.clientSecret)
        setPaymentIntentId(p.paymentIntentId)
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
    return () => { cancelled = true }
  }, [createPaymentIntent, currency])

  if (!stripeConfigured) {
    return <p className="error">Card payments aren’t configured (missing publishable key).</p>
  }
  if (error) return <p className="error" data-testid="checkout-error">{error}</p>
  if (loading || !clientSecret || !paymentIntentId) {
    return <p className="shop-state" data-testid="stripe-preparing">Preparing secure payment…</p>
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
      <PayForm total={total} currency={currency} paymentIntentId={paymentIntentId} onPaid={onPaid} />
    </Elements>
  )
}

function PayForm({
  total, currency, paymentIntentId, onPaid,
}: { total: number; currency: string; paymentIntentId: string; onPaid: (o: PaidOrder) => void }) {
  const stripe = useStripe()
  const elements = useElements()
  const [submitting, setSubmitting] = useState(false)
  // The card was already charged on a prior attempt. If finalize then
  // failed, the retry must ONLY re-finalize — never re-confirm a paid
  // PaymentIntent (which would error / risk a double action).
  const [paid, setPaid] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [finalize] = useMutation(FINALIZE_STRIPE_CHECKOUT)

  // Create the paid order (idempotent with the Stripe webhook server-side).
  async function finalizeOrder() {
    try {
      const { data } = await finalize({ variables: { paymentIntentId } })
      const payload = data?.finalizeStripeCheckout
      const ferr = payload?.userErrors?.[0]?.message
      if (ferr) { setError(ferr); setSubmitting(false); return }
      onPaid(payload.order)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setSubmitting(false)
    }
  }

  async function pay(e: React.FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return
    setSubmitting(true)
    setError(null)

    // Already charged → just re-attempt the order finalization.
    if (paid) { await finalizeOrder(); return }

    // redirect: 'if_required' keeps non-3DS cards (e.g. 4242) inline so we
    // can finalize right here instead of bouncing through a return_url.
    const { error: confirmErr, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    })
    if (confirmErr) {
      setError(confirmErr.message ?? 'Payment failed')
      setSubmitting(false)
      return
    }
    if (paymentIntent?.status !== 'succeeded') {
      setError(`Payment not completed (status: ${paymentIntent?.status ?? 'unknown'})`)
      setSubmitting(false)
      return
    }

    setPaid(true) // from here a retry must not re-confirm the charge
    await finalizeOrder()
  }

  return (
    <form onSubmit={pay} className="checkout-form" data-testid="stripe-form">
      <PaymentElement />
      {error && <p className="error" data-testid="checkout-error">{error}</p>}
      <button type="submit" className="btn-primary checkout-place" disabled={!stripe || submitting} data-testid="pay-button">
        {submitting ? 'Processing…' : paid ? 'Complete order' : `Pay ${formatPrice(total, currency)}`}
      </button>
      <p className="checkout-note">Test mode — card 4242 4242 4242 4242, any future expiry + any CVC + any postcode.</p>
    </form>
  )
}
